import { UIElement } from '../ui-element';
import { eventBus } from '../../global/event-bus';
import { Labels } from '../../global/labels';
import { setInnerText, updateElement } from '../../global/ui-helpers';
import { UIBadge } from '../badge/ui-badge';
import { BrandColors } from '../../global/helpers-marketing';
import { UIIcon } from '../icon/ui-icon';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type { NullOr } from '../../global/utils.type';
import type {
    UIDialogBubbleStandaloneMode,
    UIDialogBubbleStandaloneModes,
} from './ui-dialogbubble.type';
import type { TranslationLabels } from '../../types/labels.type';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { Component } from '../../decorators/component.decorator';
import styles from './ui-dialogbubble.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIDialogBubble
 * @element ui-dialogbubble
 * @classdesc Represents a class for <code>ui-dialogbubble</code> element.
 * @property {string} badge {@attr badge} Text inside badge.
 * @property {string} label {@attr label} Label for button (accessibility).
 * @property {"on"|"off"} [standalone="on"] {@attr standalone} Use the component in standalone mode.
 * @property {HTMLSpanElement} badgeElem Shortcut to badge element.
 * @fires event:dialog-close
 * @fires event:dialog-open
 * @listens event:dialogbubble-show
 * @listens event:dialogbubble-hide
 * @listens event:dialogbubble-newmessages
 * @example
 * <ui-dialogbubble></ui-dialogbubble>
 */
@Component({ tag: 'ui-dialogbubble', styles })
export class UIDialogBubble extends UIElement {
    readonly Standalone: UIDialogBubbleStandaloneModes = {
        On: 'on',
        Off: 'off',
    };

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-dialogbubble', {
            open: 'Chat with us',
        });
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['badge'];
    }

    @Attribute(String) standalone: UIDialogBubbleStandaloneMode =
        this.Standalone.On;
    @Attribute(String) badge?: string;
    @Attribute(String) label?: string;

    @Child('ui-badge') badgeElem?: UIBadge;

    isStandalone(): boolean {
        return this.standalone !== this.Standalone.Off;
    }

    /**
     * Resolve possible count value
     * @param {string} value
     * @returns {string | null}
     */
    getNotificationsCount(value: string | number): NullOr<string> {
        if (value != null && (value as number) > 9) {
            return '9+';
        }
        if (value != null && (value as number) <= 0) {
            return null;
        }
        return String(value);
    }

    /**
     * Handle notification changes
     * @param {CustomEvent} event
     * @private
     */
    private handleNewMessagesChange = (event: CustomEvent): void => {
        this.setAttributes({
            badge: event.detail?.count || null,
        });
    };

    /**
     * Show dialog bubble handle.
     * @private
     */
    private handleShowAction = (): void => {
        this.show();
        if (this.isStandalone()) {
            this.dispatchCustomEvent('dialog-close', null);
        }
    };

    /**
     * Hide dialog bubble handle.
     * @private
     */
    private handleHideAction = (): void => {
        this.hide();
    };

    /**
     * Handle click action to hide bubble and dispatch.
     * @private
     */
    private handleClickAction = (): void => {
        this.hide();
        if (this.isStandalone()) {
            this.dispatchCustomEvent('dialog-open', null);
        }
    };

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        const count = this.getNotificationsCount(newValue || '');
        switch (name) {
            case 'badge':
                this.setAttributes({ badge: count });
                if (this.badgeElem) {
                    setInnerText(this.badgeElem, count ?? '');
                }
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.updateElement({
            children: [
                {
                    tagName: 'button',
                    attributes: {
                        'type': 'button',
                        'role': 'region',
                        'aria-haspopup': 'true',
                        'aria-label': this.label || UIDialogBubble.labels.open,
                    },
                    children: [
                        {
                            tagName: 'ui-icon',
                            attributes: {
                                glyph: 'dialog',
                                bgcolor: UIIcon.colors.TURQUOISE,
                            },
                        },
                    ],
                },
                {
                    tagName: 'ui-badge',
                    attributes: {
                        'role': 'status',
                        'aria-live': 'polite',
                        'color': BrandColors.Pineapple,
                        'layout': UIBadge.Layout.Pin,
                    },
                    children: [this.getNotificationsCount(this.badge!)],
                },
            ],
        });
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        if (!this.isStandalone()) {
            return;
        }

        const button = this.querySelector<HTMLButtonElement>('button');
        if (button !== null) {
            updateElement(button, {
                events: {
                    click: this.handleClickAction,
                },
            });
        }

        eventBus.addEventListener('dialogbubble-show', this.handleShowAction);
        eventBus.addEventListener('dialogbubble-hide', this.handleHideAction);
        eventBus.addEventListener(
            'dialogbubble-newmessages',
            this.handleNewMessagesChange as () => void
        );
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        if (!this.isStandalone()) {
            return;
        }
        eventBus.removeEventListener(
            'dialogbubble-show',
            this.handleShowAction
        );
        eventBus.removeEventListener(
            'dialogbubble-hide',
            this.handleHideAction
        );
        eventBus.removeEventListener(
            'dialogbubble-newmessages',
            this.handleNewMessagesChange as () => void
        );
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        if (!this.isStandalone()) {
            return;
        }
        eventBus.addEventListener('dialogbubble-show', this.handleShowAction);
        eventBus.addEventListener('dialogbubble-hide', this.handleHideAction);
        eventBus.addEventListener(
            'dialogbubble-newmessages',
            this.handleNewMessagesChange as () => void
        );
    }
}
